import type { FC } from "react";

interface NearbyFacilityItemProps {
    name: string;
    distance: number;
    address1: string;
    address2: string | null;
    city: string;
    state: string;
    zip: string;
    facilityUrl: string;
    imageUrl?: string;
    unitTypeName?: string;
    pinNumber?: number;
    pinColor?: string;
}

interface PinIconProps {
    number: number;
    color: string;
}

// Pin icon component that matches the map marker style exactly
const PinIcon = ({ number, color }: PinIconProps) => {
    return (
        <div className="facility-pin-icon">
            <svg width="24" height="32" viewBox="0 0 24 32" aria-hidden="true">
                {/* Teardrop shape - matches the map marker */}
                <path
                    d="M12,0C5.4,0,0,5.4,0,12c0,7.2,12,20,12,20s12-12.8,12-20C24,5.4,18.6,0,12,0z"
                    fill={color}
                    className="pin-shape"
                />
                {/* Number text */}
                <text
                    x="12"
                    y="16"
                    textAnchor="middle"
                    className="pin-number"
                    fill="#ffffff"
                    fontSize="14"
                >
                    {number}
                </text>
            </svg>
        </div>
    );
};

export const NearbyFacilityItem: FC<NearbyFacilityItemProps> = ({
    name,
    distance,
    address1,
    address2,
    city,
    state,
    zip,
    facilityUrl,
    imageUrl,
    unitTypeName = "",
    pinNumber,
    pinColor = "#000000",
}) => {
    const handleViewUnits = () => {
        const params = new URLSearchParams({ unit_type_name: unitTypeName });
        window.location.href = `${facilityUrl}?${params.toString()}`;
    };

    const streetAddress = address2 ? `${address1}, ${address2}` : address1;
    const cityStateZip = `${city}, ${state} ${zip}`;

    return (
        <div className="nearby-facility-item tw:flex tw:flex-col tw:sm:flex-row tw:items-start tw:bg-gray-100 tw:p-4 tw:rounded-md tw:shadow-sm">
            {imageUrl && (
                <img
                    src={imageUrl}
                    alt={`${name} facility`}
                    className="nearby-facility-item__image tw:sm:w-20 tw:sm:h-20 tw:w-full tw:h-[140px] tw:object-cover tw:rounded-md tw:shrink-0"
                    loading="lazy"
                />
            )}
            <div className="nearby-facility-item__info tw:flex-1 tw:sm:ml-4 tw:min-w-0 tw:mt-2 tw:sm:mt-0">
                <h3 className="nearby-facility-item__name tw:font-bold tw:mb-1">
                    {name}
                </h3>
                <p className="nearby-facility-item__distance tw:text-sm tw:text-gray-600 tw:mb-1">
                    (<strong>{distance} miles</strong> from this location)
                </p>
                <div className="tw:flex tw:items-start tw:gap-2">
                    {pinNumber && (
                        <>
                            <PinIcon number={pinNumber} color={pinColor} />
                            <span className="tw:sr-only">
                                Location {pinNumber}
                            </span>
                        </>
                    )}
                    <div className="tw:flex-1">
                        <p className="nearby-facility-item__street-address tw:text-sm tw:text-gray-500 tw:mb-1">
                            {streetAddress}
                        </p>
                        <p className="nearby-facility-item__city-state-zip tw:text-sm tw:text-gray-500 tw:mb-1">
                            {cityStateZip}
                        </p>
                    </div>
                </div>
            </div>
            <div className="nearby-facility-item__action tw:my-auto tw:sm:ml-4 tw:shrink-0 tw:mt-4 tw:sm:mt-0 tw:w-full tw:sm:w-auto">
                <button
                    type="button"
                    className="nearby-facility-item__button tw:inline-flex tw:items-center tw:justify-center tw:whitespace-nowrap tw:text-sm tw:font-medium tw:transition-colors tw:focus-visible:outline-none tw:focus-visible:ring-1 tw:focus-visible:ring-ring tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:bg-primary tw:text-primary-foreground tw:shadow tw:hover:bg-primary/90 tw:h-9 tw:px-4 tw:py-2 tw:rounded-md find-nearby-btn"
                    onClick={handleViewUnits}
                    aria-label={`View available units at ${name}`}
                >
                    See Units
                </button>
            </div>
        </div>
    );
};
